<template>
	<div class="fixed inset-0 overflow-y-scroll bg-black bg-opacity-50 z-20" style="backdrop-filter: blur(5px);">
		<div class="flex items-start justify-center text-center my-24">
			<div
				class="bg-white rounded-lg text-center shadow-xl w-11/12 md:w-11/12 lg:w-11/12 xl:w-11/12 xxl:w-11/12 2xl:w-2/3 justify-center px-10 py-4 relative"
				style="min-height: 200px"
				role="dialog"
				aria-modal="true"
			>
				<div>
					<OnboardingStepWidget :filtered-steps="filteredSteps.length" :on-boarding-step="onBoardingStep" />
					<div class="text-start justify-start flex flex-col border-b-2 border-connectProBlue pb-4">
						<div class="text-connectProBlue font-bold justify-start flex" style="font-size: 22px">
							{{ title }}
						</div>
						<div class="pt-2 flex justify-start whitespace-pre-line" style="font-size: 16px; text-align: start">
							{{ getSubtitle }}
						</div>
					</div>

					<div class="flex flex-grow justify-center py-4">
						<component :is="currentStepComponent" v-bind="currentStepProps" :ref="currentStepRef" v-on="currentStepListeners" />
					</div>
					<Footer
						class="mt-10"
						@backButtonClick="backButtonClicked"
						@skipButtonClick="skipButtonClicked"
						:disable-continue="disableButton"
						:is-continue-button="true"
						:is-skippable="evaluateSkippable"
						:is-back-button="onBoardingStep > 0 && onBoardingStep + 1 !== filteredSteps.length"
					>
						<template #continueButton>
							<SaveButton @click="continueButtonClicked"
								>{{ onBoardingStep + 1 !== filteredSteps.length ? $t('general.next') : $t('close') }}
							</SaveButton>
						</template>
					</Footer>
				</div>
				<div v-show="isLoading" class="loading-overlay bg-black bg-opacity-15">
					<LoadingSpinner />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Authentication from '@/mixins/Authentication';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import Footer from '@/components/damagecases/Footer.vue';
import OnboardingWelcome from '@/views/onboarding/components/OnboardingWelcome.vue';
import OnboardingCompanyDataForm from '@/views/onboarding/components/OnboardingCompanyDataForm.vue';
import OnboardingCompanyDataLegalForm from '@/views/onboarding/components/OnboardingCompanyLegalDataForm.vue';
import OnboardingCompanyAssetsForm from '@/views/onboarding/components/OnboardingCompanyAssetsForm.vue';
import OnboardingCompanyTOCForm from '@/views/onboarding/components/OnboardingCompanyTOCForm.vue';
import OnboardingCompanyVariablesForm from '@/views/onboarding/components/OnboardingCompanyVariablesForm.vue';
import OnboardingCompanyOpeningHoursForm from '@/views/onboarding/components/OnboardingCompanyOpeningHoursForm.vue';
import OnboardingCompanyRentalCarsPricing from '@/views/onboarding/components/OnboardingCompanyRentalCarsPricing.vue';
import OnboardingCompanyFeeAgreement from '@/views/onboarding/components/OnboardingCompanyFeeAgreement.vue';
import OnboardingCompanyExtraCosts from '@/views/onboarding/components/OnboardingCompanyExtraCosts.vue';
import OnboardingTermsAndConditions from '@/views/onboarding/components/OnboardingTermsAndConditions.vue';
import OnboardingCompleted from '@/views/onboarding/components/OnboardingCompleted.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import OnboardingCompanyRentalCarsAdd from '@/views/onboarding/components/OnboardingCompanyRentalCarsAdd.vue';
import OnboardingServiceProviderDamageAssessor from '@/views/onboarding/components/OnboardingServiceProviderDamageAssessor.vue';
import OnboardingServiceProviderLawyerOffice from '@/views/onboarding/components/OnboardingServiceProviderLawyerOffice.vue';
import OnboardingStripeData from '@/views/onboarding/components/OnboardingStripeData.vue';
import OnboardingCompanyRentalCarsExtraCosts from '@/views/onboarding/components/OnboardingCompanyRentalCarsExtraCosts.vue';
import OnboardingRepository from '@/api/repositories/OnboardingRepository';
import { loadStripe } from '@stripe/stripe-js';
import PaymentRepository from '@/api/repositories/PaymentRepository';
//import OnboardingConnectContract from '@/views/onboarding/components/OnboardingConnectContract.vue';
import OnboardingStepWidget from '@/views/onboarding/components/OnboardingStepWidget.vue';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import { PDFDocumentTypes } from '@/api/models/PDFDocumentTypes';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';
import OnboardingCompanyPDFBlueprints from '@/views/onboarding/components/OnboardingCompanyPDFBlueprints.vue';

export default {
	name: 'OnboardingModal',
	mixins: [Authentication, AppContainerMixin],
	components: {
		OnboardingStepWidget,
		SaveButton,
		OnboardingCompleted,
		//OnboardingConnectContract,
		OnboardingTermsAndConditions,
		OnboardingCompanyExtraCosts,
		OnboardingCompanyFeeAgreement,
		OnboardingStripeData,
		OnboardingCompanyRentalCarsAdd,
		OnboardingCompanyRentalCarsPricing,
		OnboardingCompanyRentalCarsExtraCosts,
		OnboardingServiceProviderDamageAssessor,
		OnboardingServiceProviderLawyerOffice,
		OnboardingCompanyOpeningHoursForm,
		OnboardingCompanyVariablesForm,
		OnboardingCompanyTOCForm,
		OnboardingCompanyAssetsForm,
		OnboardingCompanyDataLegalForm,
		OnboardingCompanyDataForm,
		OnboardingCompanyPDFBlueprints,
		OnboardingWelcome,
		LoadingSpinner,
		Footer,
	},
	data() {
		return {
			customer: null,
			isLoading: true,
			onBoardingStep: 0,
			disableButton: false,
			company: {
				city: {
					zipCode: null,
					name: null,
				},
				bankAccount: {},
			},
			options: [],
			repairShopSteps: [],
			assessorSteps: [],
			lawyerSteps: [],
			allApiSteps: [],
			stripeTimerId: null,
		};
	},
	mounted() {
		window.document.documentElement.classList.add('disableScroll');
		this.repairShopSteps = [
			{ component: 'OnboardingWelcome' },
			{ component: 'OnboardingCompanyDataForm', ref: 'onboardingCompanyData', props: { value: this.company } },
			{ component: 'OnboardingCompanyDataLegalForm', ref: 'onboardingCompanyDataLegal', props: { value: this.company } },
			{ component: 'OnboardingCompanyAssetsForm', ref: 'onboardingCompanyAssets', props: { value: this.company } },
			{ component: 'OnboardingCompanyTOCForm', ref: 'onboardingCompanyToc', props: { value: this.company } },
			{ component: 'OnboardingCompanyVariablesForm', ref: 'onboardingCompanyVariables' },
			{ component: 'OnboardingCompanyOpeningHoursForm', ref: 'onboardingCompanyOpeningHours', props: { value: this.company } },
			{ component: 'OnboardingCompanyRentalCarsPricing', ref: 'onboardingCompanyRentalCarsPricing', props: { value: this.company } },
			{ component: 'OnboardingCompanyRentalCarsExtraCosts', ref: 'onboardingCompanyRentalCarsExtraCosts' },
			{ component: 'OnboardingCompanyRentalCarsAdd', ref: 'onboardingCompanyRentalCarsAdd' },
			{ component: 'OnboardingServiceProviderDamageAssessor', ref: 'onboardingCompanyServiceProviderDamageAssessor' },
			{ component: 'OnboardingServiceProviderLawyerOffice', ref: 'onboardingCompanyServiceProviderLawyerOffice' },
			{ component: 'OnboardingStripeData', ref: 'onboardingCompanyStripeData', emits: ['checkStripeConnection'] },
			{
				component: 'OnboardingTermsAndConditions',
				ref: 'onboardingTermsAndConditions',
				emits: [
					'downloadTos',
					'downloadDataProtection',
					'downloadSupportPolicy',
					'downloadConsentPolicy',
					'downloadDpaAvvFile',
					'downloadDpaAvvAnnex1File',
					'downloadDpaAvvAnnex2File',
				],
			},
			//{ component: 'OnboardingConnectContract', ref: 'onboardingConnectContract', emits: ['downloadContract'] },
			{ component: 'OnboardingCompleted' },
		];
		this.assessorSteps = [
			{ component: 'OnboardingWelcome' },
			{ component: 'OnboardingCompanyDataForm', ref: 'onboardingCompanyData', props: { value: this.company } },
			{ component: 'OnboardingCompanyDataLegalForm', ref: 'onboardingCompanyDataLegal', props: { value: this.company } },
			{ component: 'OnboardingCompanyAssetsForm', ref: 'onboardingCompanyAssets', props: { value: this.company } },
			{ component: 'OnboardingCompanyTOCForm', ref: 'onboardingCompanyToc', props: { value: this.company } },
			{ component: 'OnboardingCompanyPDFBlueprints', ref: 'onboardingCompanyBlueprints' },
			{ component: 'OnboardingCompanyFeeAgreement', ref: 'onboardingCompanyFeeAgreement' },
			{ component: 'OnboardingCompanyExtraCosts', ref: 'onboardingCompanyExtraCosts' },
			{ component: 'OnboardingCompleted' },
		];
		this.lawyerSteps = [
			{ component: 'OnboardingWelcome' },
			{ component: 'OnboardingCompanyDataForm', ref: 'onboardingCompanyData', props: { value: this.company } },
			{ component: 'OnboardingCompanyDataLegalForm', ref: 'onboardingCompanyDataLegal', props: { value: this.company } },
			{ component: 'OnboardingCompanyAssetsForm', ref: 'onboardingCompanyAssets', props: { value: this.company } },
			{ component: 'OnboardingCompleted' },
		];

		this.checkStepsStored();
		this.isLoading = false;
	},
	methods: {
		checkStepsStored() {
			const stepsStatus = this.onboardingStatus; // this.$store.getters.getUser.onboarding?.steps || {};

			const roleSpecificSteps = [];
			const commonSteps = [
				{ key: 'companyData', component: 'OnboardingCompanyDataForm' },
				{ key: 'legalData', component: 'OnboardingCompanyDataLegalForm' },
				{ key: 'companyAssets', component: 'OnboardingCompanyAssetsForm' },
			];

			if (this.isUserAllowedInLevel2) {
				roleSpecificSteps.push(
					{ key: 'companyFees', component: 'OnboardingCompanyFeeAgreement' },
					{ key: 'companyExtraCosts', component: 'OnboardingCompanyExtraCosts' },
					{ key: 'companyTOC', component: 'OnboardingCompanyTOCForm' },
					{ key: 'pdfBlueprints', component: 'OnboardingCompanyPDFBlueprints' },
				);
			}

			if (this.isUserAllowedInLevel5or6) {
				roleSpecificSteps.push(
					{ key: 'companyVariables', component: 'OnboardingCompanyVariablesForm' },
					{ key: 'stripeAccount', component: 'OnboardingStripeData' },
					{ key: 'openingHours', component: 'OnboardingCompanyOpeningHoursForm' },
					{ key: 'companyTOC', component: 'OnboardingCompanyTOCForm' },
					{ key: 'rentalCarsPricing', component: 'OnboardingCompanyRentalCarsPricing' },
					{ key: 'rentalCarsExtraCosts', component: 'OnboardingCompanyRentalCarsExtraCosts' },
					{ key: 'rentalCarsAdd', component: 'OnboardingCompanyRentalCarsAdd' },
					{ key: 'serviceProviderDamageAssessor', component: 'OnboardingServiceProviderDamageAssessor' },
					{ key: 'serviceProviderLawyerOffice', component: 'OnboardingServiceProviderLawyerOffice' },
					{ key: 'termsAndConditions', component: 'OnboardingTermsAndConditions' },
					{ key: 'contractSignature', component: 'OnboardingConnectContract' },
				);
			}

			const allSteps = [...commonSteps, ...roleSpecificSteps];
			this.allApiSteps = allSteps;

			const hasAnyCompleted = allSteps.some(s => !!stepsStatus[s.key]);

			if (!hasAnyCompleted) {
				this.onBoardingStep = 0;
				return;
			}

			const lastIndex = this.filteredSteps.length - 1;

			const firstIncompleteIndex = this.filteredSteps.findIndex((step, index) => {
				if (index === lastIndex) return false; // letzten nie überspringen

				const apiStep = allSteps.find(el => el.component === step.component);
				if (!apiStep) return false; // z.B. Welcome oder sonstige rein UI-Steps

				const isCompleted = !!stepsStatus[apiStep.key];
				return !isCompleted;
			});

			if (firstIncompleteIndex === -1) {
				this.onBoardingStep = lastIndex;
			} else {
				this.onBoardingStep = firstIncompleteIndex;
			}
		},
		moveToNextStep() {
			const stepsStatus = this.onboardingStatus;
			const lastIndex = this.filteredSteps.length - 1;

			let nextIndex = this.onBoardingStep + 1;

			while (nextIndex < lastIndex) {
				const step = this.filteredSteps[nextIndex];
				const apiStep = this.allApiSteps.find(el => el.component === step.component);

				if (!apiStep) break;

				const isCompleted = !!stepsStatus[apiStep.key];

				if (!isCompleted) {
					break;
				}

				nextIndex++;
			}

			if (nextIndex <= lastIndex) {
				this.onBoardingStep = nextIndex;
			} else {
				console.log('Onboarding abgeschlossen');
				this.onBoardingStep = lastIndex;
			}
		},
		backButtonClicked() {
			if (this.onBoardingStep > 0) {
				this.onBoardingStep--;
			}
			// this.onBoardingStep = this.getOnboardingSteps[this.onBoardingStep - 2];
		},
		async continueButtonClicked() {
			switch (this.filteredSteps[this.onBoardingStep]?.component) {
				case 'OnboardingWelcome':
					this.moveToNextStep();
					break;
				case 'OnboardingCompanyDataForm':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								const response = {
									name: result.name,
									ownerFirstname: result.ownerFirstname,
									ownerLastname: result.ownerLastname,
									street: result.street,
									houseNumber: result.houseNumber ?? '',
									city: result.city,
									email: result.email,
								};

								if (this.isUserAllowedInLevel2) {
									response.mobileNumber = result.mobileNumber;
								} else if (this.isUserAllowedInLevel5or6) {
									response.phoneNumber = result.phoneNumber;
								} else if (this.isUserAllowedInLevel3) {
									response.phoneNumber = result.phoneNumber;
									//response.fax = result.fax;
								}

								new OnboardingRepository()
									.updateCompanyData(response)
									.then(response => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingCompanyDataLegalForm':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								//console.log(result);
								const response = {
									bankAccount: result.bankAccount,
									homepage: result.homepage,
								};

								if (this.isUserAllowedInLevel2) {
									response.taxNumber = result.taxNumber ?? null;
								} else if (this.isUserAllowedInLevel5or6) {
									response.taxNumber = result.taxNumber ?? null;
									response.amtsgericht = result.amtsgericht ?? null;
									response.hr = result.hr?.value ?? null;
									response.hr_number = result.hrNumber ?? null;
								} else if (this.isUserAllowedInLevel3) {
									response.taxNumber = result.taxNumber ?? null;
									response.sales_tax = result.sales_tax ?? null;
									response.phoneNumber = result.phoneNumber;
									//response.fax = result.fax;
								}

								new OnboardingRepository()
									.updateCompanyLegalData(response)
									.then(response => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingCompanyAssetsForm':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent) {
							const result = currentComponent.getData();
							if (!result.companyLogo) {
								if (!result.letterHeader) {
									return await this.$swal(this.$t('general.error').toString(), this.$t('upload_logo_or_skip').toString(), 'error');
								}
							} else if (!result.logoPosition) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('chose_logo_position').toString(), 'error');
							}

							await new OnboardingRepository()
								.updateCompanyAssets({
									companyLogo: result.companyLogo,
									logoPosition: result.logoPosition,
									letterHeader: result.letterHeader,
								})
								.then(response => {
									this.isLoading = false;
									this.moveToNextStep();
								})
								.catch(e => {
									this.isLoading = false;
									this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
								});
						}
					}
					break;
				case 'OnboardingCompanyTOCForm':
					{
						new OnboardingRepository()
							.updatePDFAppendix()
							.then(_ => {
								this.isLoading = false;
								this.moveToNextStep();
							})
							.catch(e => {
								this.isLoading = false;
								this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
							});
					}
					break;
				case 'OnboardingCompanyPDFBlueprints':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updatePDFBlueprints(result)
										.then(_ => {
											this.isLoading = false;
											this.moveToNextStep();
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								}
							}
						}
					}
					break;
				case 'OnboardingCompanyVariablesForm':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updateCompanyVariables(result)
										.then(response => {
											this.isLoading = false;
											this.moveToNextStep();
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								}
							}
						}
					}
					break;
				case 'OnboardingCompanyOpeningHoursForm':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								for (const key in result.openingHours) {
									if (result.openingHours[key] === '') {
										result.openingHours[key] = null;
									}
								}

								await new OnboardingRepository()
									.updateOpeningHours(result.id, result.openingHours)
									.then(response => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingCompanyRentalCarsPricing':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								new OnboardingRepository()
									.updateRentalCarsPricing(result)
									.then(response => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingCompanyRentalCarsExtraCosts':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updateRentalCarsExtraCosts(result)
										.then(response => {
											this.isLoading = false;
											this.moveToNextStep();
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								} else {
									this.isLoading = false;
								}
							}
						}
					}
					break;
				case 'OnboardingCompanyRentalCarsAdd':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('fill_in_or_skip').toString(), 'error');
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updateRentalCars(result)
										.then(response => {
											this.isLoading = false;
											this.$swal({
												title: this.$t('sure').toString(),
												text: this.$t('data_saved_add_other', { type: 'einen weiteren Mietwagen' }),
												icon: 'success',
												cancelButtonText: this.$t('general.no').toString(),
												showCancelButton: true,
												confirmButtonText: this.$t('general.yes').toString(),
											}).then(result => {
												if (result.value) {
													currentComponent.clearForm();
												} else {
													this.skipButtonClicked();
												}
											});
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								} else {
									this.isLoading = false;
								}
							}
						}
					}
					break;
				case 'OnboardingServiceProviderDamageAssessor':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('fill_in_or_skip').toString(), 'error');
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updateDamageAssessors(result)
										.then(response => {
											this.isLoading = false;
											this.$swal({
												title: this.$t('sure').toString(),
												text: this.$t('data_saved_add_other', { type: 'einen weiteren Gutachter' }),
												icon: 'success',
												cancelButtonText: this.$t('general.no').toString(),
												showCancelButton: true,
												confirmButtonText: this.$t('general.yes').toString(),
											}).then(result => {
												if (result.value) {
													currentComponent.clearForm();
												} else {
													this.skipButtonClicked();
												}
											});
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								} else {
									this.isLoading = false;
								}
							}
						}
					}
					break;
				case 'OnboardingServiceProviderLawyerOffice':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('fill_in_or_skip').toString(), 'error');
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								if (result) {
									new OnboardingRepository()
										.updateLawyerOffices(result)
										.then(response => {
											this.isLoading = false;
											this.$swal({
												title: this.$t('sure').toString(),
												text: this.$t('data_saved_add_other', { type: 'eine weitere Kanzlei' }),
												icon: 'success',
												cancelButtonText: this.$t('general.no').toString(),
												showCancelButton: true,
												confirmButtonText: this.$t('general.yes').toString(),
											}).then(result => {
												if (result.value) {
													currentComponent.clearForm();
												} else {
													this.skipButtonClicked();
												}
											});
										})
										.catch(e => {
											this.isLoading = false;
											this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
										});
								} else {
									this.isLoading = false;
								}
							}
						}
					}
					break;
				case 'OnboardingCompanyFeeAgreement':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();

								new OnboardingRepository()
									.updateCompanyFees(result)
									.then(_ => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingCompanyExtraCosts':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return;
							} else {
								this.isLoading = true;
								const result = currentComponent.getData();
								new OnboardingRepository()
									.updateExtraCosts(result)
									.then(_ => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingStripeData':
					{
						this.isLoading = true;
						this.checkStripeConnection(true);
					}
					break;
				case 'OnboardingTermsAndConditions':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('please_read_all_documents').toString(), 'error');
							} else {
								this.isLoading = true;
								new OnboardingRepository()
									.updateTos()
									.then(_ => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
					}
					break;
				case 'OnboardingConnectContract':
					{
						const currentComponent = this.$refs[this.currentStepRef];
						if (currentComponent && typeof currentComponent.validate === 'function') {
							const isValid = await currentComponent.validate();
							if (!isValid) {
								return await this.$swal(this.$t('general.error').toString(), this.$t('please_read_contract').toString(), 'error');
							} else {
								this.isLoading = true;
								new OnboardingRepository()
									.updateConnectContract()
									.then(_ => {
										this.isLoading = false;
										this.moveToNextStep();
									})
									.catch(e => {
										this.isLoading = false;
										this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
									});
							}
						}
						// old structure for signing doc
						// const currentComponent = this.$refs[this.currentStepRef];
						// const result = currentComponent.getData();
						// if (result) {
						// 	this.isLoading = true;
						// 	new OnboardingRepository()
						// 		.updateContract({ sign: result, id: this.getUserCompany.id }, PDFDocumentTypes.MaintenanceTOC)
						// 		.then(contractResponse => {
						// 			this.isLoading = false;
						// 			this.$swal({
						// 				title: this.$t('general.success').toString(),
						// 				text: this.$t('contract_uploaded_download'),
						// 				icon: 'success',
						// 				cancelButtonText: this.$t('general.no').toString(),
						// 				showCancelButton: true,
						// 				confirmButtonText: this.$t('general.yes').toString(),
						// 			}).then(result => {
						// 				if (result.value) {
						// 					new OnboardingRepository()
						// 						.downloadContract(contractResponse.data.uuid)
						// 						.then(response => {
						// 							const label = response.headers['content-disposition'].split('filename=')[1];
						// 							const blob = new Blob([response.data], { type: 'application/pdf' });
						// 							const url = window.URL.createObjectURL(blob);
						// 							setTimeout(function() {
						// 								const a = document.createElement('a');
						// 								a.style.display = 'none';
						// 								a.href = url;
						// 								a.download = label;
						// 								document.body.appendChild(a);
						// 								a.click();
						// 								window.URL.revokeObjectURL(url);
						// 							}, 200);
						// 							this.moveToNextStep();
						// 						})
						// 						.catch(e => {
						// 							this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
						// 						});
						// 				} else {
						// 					this.moveToNextStep();
						// 				}
						// 			});
						// 		})
						// 		.catch(e => {
						// 			this.isLoading = false;
						// 			this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
						// 		});
						// } else {
						// 	return this.$swal(this.$t('general.error').toString(), this.$t('sign_process').toString(), 'error');
						// }
					}
					break;
				case 'OnboardingCompleted': {
					this.isLoading = true;
					new OnboardingRepository()
						.updateInquired()
						.then(_ => {
							this.isLoading = false;
							this.$store.dispatch('updateAuthenticatedUser');
						})
						.catch(e => {
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
						});
				}
			}
		},
		skipButtonClicked() {
			if (this.evaluateSkippable) {
				const step = this.allApiSteps.find(apiStep => apiStep.component === this.filteredSteps[this.onBoardingStep]?.component);
				if (step) {
					new OnboardingRepository()
						.skipStep(step.key)
						.then(response => {
							this.moveToNextStep();
						})
						.catch(e => {
							this.$swal(this.$t('general.error').toString(), this.$t('step_cannot_be_skipped').toString(), 'error');
						});
				}
			}
		},
		openDocument(response) {
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const name = response.headers['content-disposition'].split('filename=')[1];
			if (this.isAppContainer) {
				const reader = new FileReader();
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(blob);
			} else {
				const resolvingUrl = URL.createObjectURL(blob);
				const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(resolvingUrl)}&title=${encodeURIComponent(name)}`;

				setTimeout(function() {
					window.open(viewerUrl, '_blank');
					window.URL.revokeObjectURL(viewerUrl);
				}, 200);
			}
		},
		downloadContract() {
			new OnboardingRepository()
				.downloadConnectContract()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		//agb zeug aktuell anfang
		downloadTos() {
			new OnboardingRepository()
				.downloadTosFile()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDataProtection() {
			new OnboardingRepository()
				.downloadDataProtection()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadSupportPolicy() {
			new OnboardingRepository()
				.downloadSupportPolicy()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadConsentPolicy() {
			new OnboardingRepository()
				.downloadConsentPolicy()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDpaAvvFile() {
			new OnboardingRepository()
				.downloadDpaAvvFile()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDpaAvvAnnex1File() {
			new OnboardingRepository()
				.downloadDpaAvvAnnex1File()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDpaAvvAnnex2File() {
			new OnboardingRepository()
				.downloadDpaAvvAnnex2File()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		//agb zeug aktuell ende
		downloadCommunityRules() {
			new OnboardingRepository()
				.downloadCommunityRules()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadAccountActivity() {
			new OnboardingRepository()
				.downloadAccountActivity()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDataProtectionUS() {
			new OnboardingRepository()
				.downloadDataProtectionUS()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		checkStripeConnection(showErrorMsg = false) {
			new OnboardingRepository()
				.updateStripeData()
				.then(response => {
					const stripeData = response.data.stripe_account;
					if (stripeData) {
						if (stripeData.customer && stripeData.subscription === false) {
							clearInterval(this.stripeTimerId);
							this.$swal({
								title: this.$t('general.hint').toString(),
								text: this.$t('stripe_subscription_not_saved').toString(),
								icon: 'warning',
							}).then(_ => {
								this.clearStripeTimer(false);
							});
						} else {
							this.clearStripeTimer();
						}
					}
				})
				.catch(e => {
					if (showErrorMsg) {
						this.isLoading = false;
						this.$swal(this.$t('general.error').toString(), this.$t('stripe_account_not_found').toString(), 'error');
					}
				});
		},
		clearStripeTimer(showMessage = true) {
			clearInterval(this.stripeTimerId);
			this.isLoading = false;
			this.stripeTimerId = null;
			if (showMessage) {
				this.$swal(this.$t('general.success').toString(), this.$t('stripe_account_connected').toString(), 'success').then(_ => {
					this.moveToNextStep();
				});
			} else {
				this.moveToNextStep();
			}
		},
		handleEvent(eventName, ...args) {
			switch (eventName) {
				case 'checkStripeConnection':
					this.checkStripeConnection();
					break;
				case 'downloadContract':
					this.downloadContract();
					break;
				case 'downloadTos':
					this.downloadTos();
					break;
				case 'downloadDataProtection':
					this.downloadDataProtection();
					break;
				case 'downloadSupportPolicy':
					this.downloadSupportPolicy();
					break;
				case 'downloadConsentPolicy':
					this.downloadConsentPolicy();
					break;
				case 'downloadDpaAvvFile':
					this.downloadDpaAvvFile();
					break;
				case 'downloadDpaAvvAnnex1File':
					this.downloadDpaAvvAnnex1File();
					break;
				case 'downloadDpaAvvAnnex2File':
					this.downloadDpaAvvAnnex2File();
					break;
				/*
				case 'downloadCommunityRules':
					this.downloadCommunityRules();
					break;
				case 'downloadAccountActivity':
					this.downloadAccountActivity();
					break;
				case 'downloadDataProtectionUS':
					this.downloadDataProtectionUS();
					break;
					*/
			}
		},
	},
	computed: {
		onboardingStatus() {
			return this.$store.getters.getUser.onboarding?.steps || {};
		},
		filteredSteps() {
			if (this.isUserAllowedInLevel2) {
				return this.assessorSteps;
			} else if (this.isUserAllowedInLevel5or6) {
				return this.repairShopSteps;
			} else if (this.isUserAllowedInLevel3) {
				return this.lawyerSteps;
			} else {
				return [];
			}
		},
		currentStepListeners() {
			const emits = this.filteredSteps[this.onBoardingStep]?.emits || [];
			const listeners = {};
			emits.forEach(event => {
				listeners[event] = (...args) => this.handleEvent(event, ...args);
			});
			return listeners;
		},
		currentStepComponent() {
			return this.filteredSteps[this.onBoardingStep]?.component || null;
		},
		currentStepProps() {
			return this.filteredSteps[this.onBoardingStep]?.props || {};
		},
		currentStepRef() {
			return this.filteredSteps[this.onBoardingStep]?.ref || null;
		},
		title() {
			switch (this.filteredSteps[this.onBoardingStep]?.component) {
				case 'OnboardingWelcome':
					return this.$t('welcome_onboarding');
				case 'OnboardingCompanyDataForm':
					return this.$t('business_data');
				case 'OnboardingCompanyDataLegalForm':
					return this.$t('business_data');
				case 'OnboardingCompanyAssetsForm':
					return this.$t('company_logo') + ' / ' + this.$t('company_letter_header');
				case 'OnboardingCompanyTOCForm':
					return this.$t('form_appendix');
				case 'OnboardingCompanyPDFBlueprints':
					return this.$t('pdf_blueprint_damageassessor');
				case 'OnboardingCompanyVariablesForm':
					return this.$t('special_positions');
				case 'OnboardingCompanyOpeningHoursForm':
					return this.$t('opening_hours');
				case 'OnboardingCompanyRentalCarsPricing':
					return this.$t('accident_rental_cars');
				case 'OnboardingCompanyRentalCarsExtraCosts':
					return this.$t('extra_costs_individual_rent_options');
				case 'OnboardingCompanyRentalCarsAdd':
					return this.$t('save_new_rental_car');
				case 'OnboardingServiceProviderDamageAssessor':
					return this.$t('service_provider') + ' - ' + this.$t('apply_new_damage_assessor');
				case 'OnboardingServiceProviderLawyerOffice':
					return this.$t('service_provider') + ' - ' + this.$t('apply_new_lawyers_office');
				case 'OnboardingStripeData':
					return this.$t('stripe_account');
				case 'OnboardingCompanyFeeAgreement':
					return this.$t('fee_table');
				case 'OnboardingCompanyExtraCosts':
					return this.$t('extra_costs');
				case 'OnboardingTermsAndConditions':
					return this.$t('toc');
				//case 'OnboardingConnectContract':
				//	return this.$t('sign_contract');
				default:
					return '';
			}
		},
		getSubtitle() {
			switch (this.filteredSteps[this.onBoardingStep]?.component) {
				case 'OnboardingWelcome':
					return '';
				case 'OnboardingCompanyDataForm':
					return this.$t('please_confirm_data');
				case 'OnboardingCompanyDataLegalForm':
					return this.$t('system_form_data');
				case 'OnboardingCompanyAssetsForm':
					return this.isUserAllowedInLevel3 ? this.$t('asset_lawyer_hint') : this.$t('upload_letter_header_logo');
				case 'OnboardingCompanyTOCForm':
					return this.$t('toc_explanation');
				case 'OnboardingCompanyPDFBlueprints':
					return this.$t('pdf_blueprint_damageassessor_explanation');
				case 'OnboardingCompanyVariablesForm':
					return this.$t('onboarding_company_vars_hint');
				case 'OnboardingCompanyOpeningHoursForm':
					return this.$t('onboarding_opening_hours_hint');
				case 'OnboardingCompanyRentalCarsPricing':
					return this.$t('onboarding_rental_cars_hint');
				case 'OnboardingCompanyRentalCarsExtraCosts':
					return this.$t('onboarding_rental_cars_extra_costs_hint');
				case 'OnboardingCompanyRentalCarsAdd':
					return this.$t('rental_cars_add_hint');
				case 'OnboardingServiceProviderDamageAssessor':
					return this.$t('onboarding_service_provider');
				case 'OnboardingServiceProviderLawyerOffice':
					return this.$t('onboarding_service_provider');
				case 'OnboardingStripeData':
					return this.$t('onboarding_stripe_hint');
				case 'OnboardingCompanyFeeAgreement':
					return this.$t('fee_agreement_hint');
				case 'OnboardingCompanyExtraCosts':
					return this.$t('extra_costs_hint');
				case 'OnboardingTermsAndConditions':
					return this.$t('toc_system_explanation');
				//case 'OnboardingConnectContract':
				//	return this.$t('contract_explanation');
				default:
					return '';
			}
		},
		evaluateSkippable() {
			switch (this.filteredSteps[this.onBoardingStep]?.component) {
				case 'OnboardingWelcome':
				case 'OnboardingCompanyDataForm':
				case 'OnboardingCompanyDataLegalForm':
				case 'OnboardingCompanyVariablesForm':
				case 'OnboardingCompanyFeeAgreement':
				case 'OnboardingCompanyExtraCosts':
				case 'OnboardingTermsAndConditions':
				case 'OnboardingCompanyRentalCarsPricing':
					//case 'OnboardingConnectContract':
					return false;
				case 'OnboardingCompanyAssetsForm':
				case 'OnboardingCompanyTOCForm':
				case 'OnboardingCompanyOpeningHoursForm':
				case 'OnboardingCompanyRentalCarsAdd':
				case 'OnboardingServiceProviderDamageAssessor':
				case 'OnboardingServiceProviderLawyerOffice':
				case 'OnboardingCompanyRentalCarsExtraCosts':
					return true;
				default:
					return false;
			}
		},
	},
	watch: {
		onBoardingStep: function(newValue) {
			if (this.currentStepComponent === 'OnboardingStripeData') {
				if (this.stripeTimerId == null) {
					this.stripeTimerId = setInterval(async () => {
						this.checkStripeConnection();
					}, 10000);
				}
			}
		},
	},
	beforeDestroy() {
		window.document.documentElement.classList.remove('disableScroll');
		if (this.stripeTimerId) {
			clearInterval(this.stripeTimerId);
		}
	},
};
</script>

<style scoped>
.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 30;
}
</style>
